import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import Pagination from '../../components/Pagination/Pagination';
import { rfqApi } from '../../services/apiService';
import { useCurrency } from '../../context/CurrencyContext';
import './AdminRFQManagePage.css';

const STATUS_OPTIONS = ['new', 'in_progress', 'replied', 'closed'];
const STATUS_LABELS = { new: 'New', in_progress: 'In Progress', replied: 'Replied', closed: 'Closed' };

const AdminRFQManagePage = () => {
  const { selectedCurrency } = useCurrency();
  // Data
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Selection / Bulk
  const [selectedItems, setSelectedItems] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Action dropdown
  const [actionDropdown, setActionDropdown] = useState({ show: false, itemId: null });

  // Modals
  const [detailModal, setDetailModal] = useState({ show: false, rfq: null });
  const [notesModal, setNotesModal] = useState({ show: false, rfq: null, text: '' });

  // Fetch
  const fetchRFQs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await rfqApi.adminGetAll({ showAll: true });
      setRfqs(res.rfqs || res || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch RFQs:', err);
      setError('Failed to load RFQs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRFQs(); }, [fetchRFQs]);

  // Client-side filtering
  const filteredRFQs = rfqs.filter(r => {
    if (selectedStatus && r.status !== selectedStatus) return false;
    if (selectedCategory && r.category !== selectedCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const haystack = [r.fullName, r.email, r.details, r.category, r.subCategory, String(r.id)]
        .filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  // Unique categories from data
  const categories = [...new Set(rfqs.map(r => r.category).filter(Boolean))].sort();

  // Pagination
  const totalPages = Math.ceil(filteredRFQs.length / itemsPerPage);
  const paginatedRFQs = filteredRFQs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedStatus, selectedCategory]);

  // Selection handlers
  const handleSelectAll = (e) => {
    setSelectedItems(e.target.checked ? paginatedRFQs.map(r => r.id) : []);
  };
  const handleSelectItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Status update
  const handleStatusChange = async (id, status) => {
    try {
      await rfqApi.updateStatus(id, status);
      setRfqs(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      setActionDropdown({ show: false, itemId: null });
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.error || err.message));
    }
  };

  // Bulk status
  const handleBulkStatus = async (status) => {
    if (!selectedItems.length) return;
    if (!window.confirm(`Update ${selectedItems.length} RFQs to "${STATUS_LABELS[status]}"?`)) return;
    try {
      await rfqApi.bulkUpdateStatus(selectedItems, status);
      setRfqs(prev => prev.map(r => selectedItems.includes(r.id) ? { ...r, status } : r));
      setSelectedItems([]);
    } catch (err) {
      alert('Bulk update failed');
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (!selectedItems.length) return;
    if (!window.confirm(`Delete ${selectedItems.length} RFQs? This cannot be undone.`)) return;
    try {
      await rfqApi.bulkDelete(selectedItems);
      setRfqs(prev => prev.filter(r => !selectedItems.includes(r.id)));
      setSelectedItems([]);
    } catch (err) {
      alert('Bulk delete failed');
    }
  };

  // Admin notes
  const handleSaveNotes = async () => {
    try {
      await rfqApi.adminUpdateNotes(notesModal.rfq.id, notesModal.text);
      setRfqs(prev => prev.map(r => r.id === notesModal.rfq.id ? { ...r, adminNotes: notesModal.text } : r));
      setNotesModal({ show: false, rfq: null, text: '' });
    } catch (err) {
      alert('Failed to save notes');
    }
  };

  // Action dropdown toggle
  const toggleAction = (id) => {
    setActionDropdown(prev => prev.show && prev.itemId === id ? { show: false, itemId: null } : { show: true, itemId: id });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.arm-action-cell')) setActionDropdown({ show: false, itemId: null });
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const statusBadgeClass = (s) => `arm-status-badge arm-status-${s}`;

  return (
    <AdminLayout>
      <div className="admin-rfq-manage-content">
        {/* Page Header */}
        <div className="arm-page-header">
          <div>
            <h1 className="arm-page-title">RFQ Management</h1>
            <p className="arm-page-subtitle">{filteredRFQs.length} request{filteredRFQs.length !== 1 ? 's' : ''} found</p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="arm-list-header">
          <div className="arm-filters">
            <div className="arm-search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder="Search by buyer, category, details..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <select className="arm-filter-select" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
              <option value="">All Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
            <select className="arm-filter-select" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Bulk Actions */}
          {selectedItems.length > 0 && (
            <div className="arm-bulk-actions">
              <span className="arm-bulk-count">{selectedItems.length} selected</span>
              {STATUS_OPTIONS.map(s => (
                <button key={s} className={`arm-bulk-btn arm-bulk-${s}`} onClick={() => handleBulkStatus(s)}>{STATUS_LABELS[s]}</button>
              ))}
              <button className="arm-bulk-btn arm-bulk-delete" onClick={handleBulkDelete}>Delete</button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="arm-content">
          {loading ? (
            <div className="arm-loading">Loading RFQs...</div>
          ) : error ? (
            <div className="arm-error">{error}</div>
          ) : filteredRFQs.length === 0 ? (
            <div className="arm-empty">No RFQs found matching your criteria.</div>
          ) : (
            <>
              <div className="arm-table">
                <div className="arm-table-header">
                  <div className="arm-th arm-th-checkbox"><input type="checkbox" checked={selectedItems.length === paginatedRFQs.length && paginatedRFQs.length > 0} onChange={handleSelectAll} /></div>
                  <div className="arm-th">ID</div>
                  <div className="arm-th">Date</div>
                  <div className="arm-th">Buyer</div>
                  <div className="arm-th">Category</div>
                  <div className="arm-th">Details</div>
                  <div className="arm-th">Qty</div>
                  <div className="arm-th">Budget</div>
                  <div className="arm-th">Quotes</div>
                  <div className="arm-th">Status</div>
                  <div className="arm-th">Action</div>
                </div>
                <div className="arm-table-body">
                  {paginatedRFQs.map(rfq => (
                    <div key={rfq.id} className="arm-table-row" style={{ cursor: 'pointer' }} onClick={(e) => { if (!e.target.closest('.arm-td-checkbox') && !e.target.closest('.arm-action-cell')) { setDetailModal({ show: true, rfq }); } }}>
                      <div className="arm-td arm-td-checkbox"><input type="checkbox" checked={selectedItems.includes(rfq.id)} onChange={() => handleSelectItem(rfq.id)} /></div>
                      <div className="arm-td arm-td-id">#{rfq.id}</div>
                      <div className="arm-td">{formatDate(rfq.createdAt)}</div>
                      <div className="arm-td arm-td-buyer">
                        <span className="arm-buyer-name">{rfq.fullName || 'N/A'}</span>
                        <span className="arm-buyer-email">{rfq.email || ''}</span>
                      </div>
                      <div className="arm-td">
                        <span className="arm-category">{rfq.category || '-'}</span>
                        {rfq.subCategory && <span className="arm-subcategory">{rfq.subCategory}</span>}
                      </div>
                      <div className="arm-td arm-td-details" title={rfq.details}>{rfq.details ? (rfq.details.length > 60 ? rfq.details.substring(0, 60) + '...' : rfq.details) : '-'}</div>
                      <div className="arm-td">{rfq.quantity ? `${rfq.quantity} ${rfq.unit || ''}` : '-'}</div>
                      <div className="arm-td">{rfq.budget ? `${rfq.budgetCurrency || selectedCurrency.code} ${rfq.budget}` : '-'}</div>
                      <div className="arm-td">{rfq.quotations?.length || 0}</div>
                      <div className="arm-td"><span className={statusBadgeClass(rfq.status)}>{STATUS_LABELS[rfq.status] || rfq.status}</span></div>
                      <div className="arm-td arm-action-cell">
                        <button className="arm-more-btn" onClick={(e) => { e.stopPropagation(); toggleAction(rfq.id); }}>⋮</button>
                        {actionDropdown.show && actionDropdown.itemId === rfq.id && (
                          <div className="arm-action-menu" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => { setDetailModal({ show: true, rfq }); setActionDropdown({ show: false, itemId: null }); }}>View Details</button>
                            <button onClick={() => { setNotesModal({ show: true, rfq, text: rfq.adminNotes || '' }); setActionDropdown({ show: false, itemId: null }); }}>Admin Notes</button>
                            <div className="arm-action-divider" />
                            {rfq.status !== 'new' && <button onClick={() => handleStatusChange(rfq.id, 'new')}>Mark New</button>}
                            {rfq.status !== 'in_progress' && <button onClick={() => handleStatusChange(rfq.id, 'in_progress')}>Mark In Progress</button>}
                            {rfq.status !== 'replied' && <button onClick={() => handleStatusChange(rfq.id, 'replied')}>Mark Replied</button>}
                            {rfq.status !== 'closed' && <button onClick={() => handleStatusChange(rfq.id, 'closed')}>Mark Closed</button>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {totalPages > 1 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              )}
            </>
          )}
        </div>

        {/* Detail Modal */}
        {detailModal.show && detailModal.rfq && (
          <div className="arm-modal-overlay" onClick={() => setDetailModal({ show: false, rfq: null })}>
            <div className="arm-modal" onClick={e => e.stopPropagation()}>
              <div className="arm-modal-header">
                <h2>RFQ #{detailModal.rfq.id} Details</h2>
                <button className="arm-modal-close" onClick={() => setDetailModal({ show: false, rfq: null })}>×</button>
              </div>
              <div className="arm-modal-body">
                <div className="arm-detail-grid">
                  <div className="arm-detail-item"><label>Status</label><span className={statusBadgeClass(detailModal.rfq.status)}>{STATUS_LABELS[detailModal.rfq.status]}</span></div>
                  <div className="arm-detail-item"><label>Date</label><span>{formatDate(detailModal.rfq.createdAt)}</span></div>
                  <div className="arm-detail-item"><label>Category</label><span>{detailModal.rfq.category || '-'}</span></div>
                  <div className="arm-detail-item"><label>Sub Category</label><span>{detailModal.rfq.subCategory || '-'}</span></div>
                  <div className="arm-detail-item"><label>Quantity</label><span>{detailModal.rfq.quantity ? `${detailModal.rfq.quantity} ${detailModal.rfq.unit || ''}` : '-'}</span></div>
                  <div className="arm-detail-item"><label>Budget</label><span>{detailModal.rfq.budget ? `${detailModal.rfq.budgetCurrency || selectedCurrency.code} ${detailModal.rfq.budget}` : '-'}</span></div>
                </div>
                <div className="arm-detail-section">
                  <label>Requirement Details</label>
                  <p>{detailModal.rfq.details || 'No details provided'}</p>
                </div>
                {detailModal.rfq.attachment && (
                  <div className="arm-detail-section">
                    <label>Attachment</label>
                    <a href={detailModal.rfq.attachment} target="_blank" rel="noopener noreferrer">View Attachment</a>
                  </div>
                )}
                <div className="arm-detail-section arm-detail-buyer">
                  <h3>Buyer Information</h3>
                  <div className="arm-detail-grid">
                    <div className="arm-detail-item"><label>Name</label><span>{detailModal.rfq.fullName || '-'}</span></div>
                    <div className="arm-detail-item"><label>Email</label><span>{detailModal.rfq.email || '-'}</span></div>
                    <div className="arm-detail-item"><label>Phone</label><span>{detailModal.rfq.phoneNumber || '-'}</span></div>
                    <div className="arm-detail-item"><label>Company</label><span>{detailModal.rfq.companyName || '-'}</span></div>
                    <div className="arm-detail-item"><label>Country</label><span>{detailModal.rfq.country || '-'}</span></div>
                  </div>
                </div>
                {detailModal.rfq.targetSellerId && (
                  <div className="arm-detail-section">
                    <label>Target Seller ID</label>
                    <span>{detailModal.rfq.targetSellerId}</span>
                  </div>
                )}
                {detailModal.rfq.adminNotes && (
                  <div className="arm-detail-section">
                    <label>Admin Notes</label>
                    <p className="arm-admin-notes">{detailModal.rfq.adminNotes}</p>
                  </div>
                )}

                {/* Quotations */}
                {detailModal.rfq.quotations && detailModal.rfq.quotations.length > 0 && (
                  <div className="arm-detail-section">
                    <h3>Quotations ({detailModal.rfq.quotations.length})</h3>
                    <div className="arm-quotation-list">
                      {detailModal.rfq.quotations.map(q => (
                        <div key={q.id} className="arm-quotation-card">
                          <div className="arm-quotation-header">
                            <span>Seller #{q.sellerId}</span>
                            <span className={`arm-status-badge arm-status-${q.status}`}>{q.status}</span>
                          </div>
                          <div className="arm-quotation-body">
                            <div><strong>Price:</strong> {q.currency || selectedCurrency.code} {q.price}</div>
                            {q.deliveryTime && <div><strong>Delivery:</strong> {q.deliveryTime}</div>}
                            {q.validUntil && <div><strong>Valid Until:</strong> {formatDate(q.validUntil)}</div>}
                            {q.description && <div><strong>Description:</strong> {q.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="arm-modal-footer">
                <select value={detailModal.rfq.status} onChange={e => { handleStatusChange(detailModal.rfq.id, e.target.value); setDetailModal(prev => ({ ...prev, rfq: { ...prev.rfq, status: e.target.value } })); }}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
                <button className="arm-btn-notes" onClick={() => { setNotesModal({ show: true, rfq: detailModal.rfq, text: detailModal.rfq.adminNotes || '' }); }}>Edit Notes</button>
                <button className="arm-btn-close" onClick={() => setDetailModal({ show: false, rfq: null })}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {notesModal.show && notesModal.rfq && (
          <div className="arm-modal-overlay" onClick={() => setNotesModal({ show: false, rfq: null, text: '' })}>
            <div className="arm-modal arm-modal-sm" onClick={e => e.stopPropagation()}>
              <div className="arm-modal-header">
                <h2>Admin Notes – RFQ #{notesModal.rfq.id}</h2>
                <button className="arm-modal-close" onClick={() => setNotesModal({ show: false, rfq: null, text: '' })}>×</button>
              </div>
              <div className="arm-modal-body">
                <textarea className="arm-notes-textarea" value={notesModal.text} onChange={e => setNotesModal(prev => ({ ...prev, text: e.target.value }))} placeholder="Add internal notes about this RFQ..." rows={6} />
              </div>
              <div className="arm-modal-footer">
                <button className="arm-btn-save" onClick={handleSaveNotes}>Save Notes</button>
                <button className="arm-btn-close" onClick={() => setNotesModal({ show: false, rfq: null, text: '' })}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRFQManagePage;
